import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { 
  ProcessoVisualizacaoResponse, 
  DashboardResponse, 
  PaginacaoResponse, 
  RegistroRecebidoVisualizacaoResponse,
  RegistroRecebidoDetalheResponse,
  RegistroVisualizacaoResponse,
  RegistroVisualizacaoDetalheResponse,
  RegistroPublicacaoResponse,
  StatusRecebimentoRegistro,
  StatusIntegracaoRegistro
} from '../types/smartinteg';

@Injectable({
  providedIn: 'root'
})
export class SmartintegVisualizacaoService {
  // Backend de monitoramento na porta 7772
  private baseUrl = 'http://localhost:7772/smartinteg/api/visualizacao';

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {
    // Tentativa automática de ajuste para produção, mantendo localhost como padrão
    const rootUrl = this.configService.getApiUrlRoot();
    if (rootUrl && !rootUrl.includes('localhost')) {
        this.baseUrl = rootUrl.replace(/:[0-9]+/, ':7772') + '/smartinteg/api/visualizacao';
    }
  }

  // RF08 - Dashboard
  getDashboard(periodo: string = '24h'): Observable<DashboardResponse> {
    const params = new HttpParams().set('periodo', periodo);
    return this.http.get<DashboardResponse>(`${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/dashboard`, { params });
  }

  // RF01 - Lista processos para visualização
  listarProcessosVisualizacao(
    ativo?: boolean,
    ordenar: string = 'dtCriado',
    direcao: string = 'desc'
  ): Observable<ProcessoVisualizacaoResponse[]> {
    let params = new HttpParams()
      .set('ordenar', ordenar)
      .set('direcao', direcao);

    // if (ativo !== null && ativo !== undefined) {
    //   params = params.set('ativo', ativo.toString());
    // }

    return this.http.get<ProcessoVisualizacaoResponse[]>(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/processos`,
      { params }
    );
  }

  // RF03 - Lista registros recebidos
  listarRegistrosRecebidos(
    processoId: string,
    status?: StatusRecebimentoRegistro | null,
    dataInicio?: Date | null,
    dataFim?: Date | null,
    busca?: string,
    page: number = 0,
    size: number = 20,
    ordenar: string = 'dataRecebimento',
    direcao: string = 'desc'
  ): Observable<PaginacaoResponse<RegistroRecebidoVisualizacaoResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('ordenar', ordenar)
      .set('direcao', direcao);

    if (status) params = params.set('status', status);
    if (dataInicio) params = params.set('dataInicio', dataInicio.toISOString());
    if (dataFim) params = params.set('dataFim', dataFim.toISOString());
    if (busca) params = params.set('busca', busca);

    return this.http.get<PaginacaoResponse<RegistroRecebidoVisualizacaoResponse>>(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/processos/${processoId}/registros-recebidos`,
      { params }
    );
  }

  // RF04 - Detalhes de registro recebido
  getDetalheRegistroRecebido(id: number): Observable<RegistroRecebidoDetalheResponse> {
    return this.http.get<RegistroRecebidoDetalheResponse>(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/registros-recebidos/${id}`
    );
  }

  // RF05 - Lista registros de visualização
  listarRegistrosVisualizacao(
    processoId: string,
    status?: StatusIntegracaoRegistro | null,
    mapeamentoId?: number | null,
    dataInicio?: Date | null,
    dataFim?: Date | null,
    busca?: string,
    page: number = 0,
    size: number = 20,
    ordenar: string = 'criadoEm',
    direcao: string = 'desc'
  ): Observable<PaginacaoResponse<RegistroVisualizacaoResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('ordenar', ordenar)
      .set('direcao', direcao);

    if (status) params = params.set('status', status);
    if (mapeamentoId) params = params.set('mapeamentoId', mapeamentoId.toString());
    if (dataInicio) params = params.set('dataInicio', dataInicio.toISOString());
    if (dataFim) params = params.set('dataFim', dataFim.toISOString());
    if (busca) params = params.set('busca', busca);

    return this.http.get<PaginacaoResponse<RegistroVisualizacaoResponse>>(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/processos/${processoId}/registros-visualizacao`,
      { params }
    );
  }

  // RF06 - Detalhes de registro de visualização
  getDetalheRegistroVisualizacao(id: number): Observable<RegistroVisualizacaoDetalheResponse> {
    return this.http.get<RegistroVisualizacaoDetalheResponse>(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/registros-visualizacao/${id}`
    );
  }

  // RF07 - Lista registros de publicação
  listarRegistrosPublicacao(
    processoId: string,
    publicacaoConfigId?: number | null,
    dataInicio?: Date | null,
    dataFim?: Date | null,
    busca?: string,
    page: number = 0,
    size: number = 20,
    ordenar: string = 'criadoEm',
    direcao: string = 'desc'
  ): Observable<PaginacaoResponse<RegistroPublicacaoResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('ordenar', ordenar)
      .set('direcao', direcao);

    if (publicacaoConfigId) params = params.set('publicacaoConfigId', publicacaoConfigId.toString());
    if (dataInicio) params = params.set('dataInicio', dataInicio.toISOString());
    if (dataFim) params = params.set('dataFim', dataFim.toISOString());
    if (busca) params = params.set('busca', busca);

    return this.http.get<PaginacaoResponse<RegistroPublicacaoResponse>>(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/processos/${processoId}/registros-publicacao`,
      { params }
    );
  }

  // RF09 - Exportação
  exportarDados(
    processoId: string,
    tipo: 'recebidos' | 'visualizacao' | 'publicacao',
    formato: 'csv' | 'xlsx' | 'json' = 'csv',
    status?: string | null,
    dataInicio?: Date | null,
    dataFim?: Date | null,
    campos?: string[]
  ): Observable<Blob> {
    let params = new HttpParams()
      .set('tipo', tipo)
      .set('formato', formato);

    if (status) params = params.set('status', status);
    if (dataInicio) params = params.set('dataInicio', dataInicio.toISOString());
    if (dataFim) params = params.set('dataFim', dataFim.toISOString());
    if (campos && campos.length > 0) {
      campos.forEach(campo => {
        params = params.append('campos', campo);
      });
    }

    return this.http.get(
      `${this.configService.getApiUrlRoot()}/smartinteg/visualizacao/processos/${processoId}/exportar`,
      {
        params,
        responseType: 'blob'
      }
    );
  }
}
